'use client'

import { useEffect } from 'react'

import { decodeEntities } from '@/app/_lib/services/ClientUtil'
import { addRecentlyViewedProduct } from '@/app/_lib/services/RecentlyViewedService'

import ProductCarousel from './ProductCarousel'
import Breadcrumbs from '@/app/_components/Breadcrumbs'
import SimilarProductsCarousel from '@/app/_components/SimilarProductsCarousel'
import RecentlyViewedProducts from '@/app/_components/RecentlyViewedProducts'
import Reviews from './Reviews'

/**
 * Simplified product container for unavailable (discontinued) products.
 *
 * Shows:
 * - Breadcrumbs
 * - Product images
 * - Product name, designer, supplier (no links)
 * - Unavailability notice
 * - Similar products
 * - Recently viewed
 * - Reviews (historical)
 *
 * Does NOT show:
 * - ProductToolbar (options, pricing, add to cart)
 * - Favorite button, sale badge, best seller badge
 * - Supplier/designer spotlights
 * - Trade pricing
 * - Product attributes
 * - Variants carousel
 * - Related products (likely also discontinued)
 */
const ProductContainerUnavailable = ({ pageAssets }) => {
    const sub_product = pageAssets.sub_product
    const similarProducts = pageAssets.similarProducts || []

    // Track in localStorage for "recently viewed"
    useEffect(() => {
        if (sub_product?.sub_product_id) {
            addRecentlyViewedProduct(sub_product)
        }
    }, [sub_product?.sub_product_id])

    return (
        <div className="mt-6 lg:mt-3 site-padding">
            <div className="flex flex-wrap gap-x-10 mb-6">

                {/* Breadcrumbs */}
                <div className="basis-full order-2 lg:order-1 lg:mb-4">
                    <Breadcrumbs breadcrumbs={sub_product.breadcrumbs} product_id={sub_product.product_id} />
                </div>

                {/* Left column - Image and description */}
                <div className="contents lg:block lg:flex-1 lg:order-2 lg:min-w-0">
                    {/* Image carousel */}
                    <div className="basis-full w-full min-w-0 order-1 lg:basis-auto lg:w-auto">
                        {sub_product.images?.length > 0 && (
                            <ProductCarousel
                                sub_product={sub_product}
                                productImages={sub_product.images}
                                video={sub_product.video}
                            />
                        )}
                    </div>

                    {/* Description */}
                    <div className="basis-full order-4 lg:basis-auto lg:mt-10">
                        <div className="leading-7 text-3xl font-extralight capitalize border-b-1 border-gray-8 mb-5 pb-3">
                            {decodeEntities(sub_product.sub_product_name)}
                        </div>
                        <p
                            className="text-justify text-lg md:text-base leading-7 md:leading-6 first-letter:text-5xl first-letter:text-primary mb-8"
                            dangerouslySetInnerHTML={{ __html: sub_product.description?.replace(/\n/g, '<br />') || '' }}
                        />
                    </div>
                </div>

                {/* Right column - Product info */}
                <div className="basis-full order-3 lg:order-3 lg:basis-[360px] xl:basis-[400px] 2xl:basis-[420px] lg:shrink-0 min-w-0">
                    <div className="mb-6">
                        <div className="mb-2">
                            <h1 className="text-3xl lg:text-4xl capitalize leading-tight font-light">
                                {decodeEntities(sub_product.sub_product_name)}
                            </h1>
                            <span className="text-light text-2xs">
                                {sub_product.designer?.designer_name && sub_product.supplier?.supplier_name ? (
                                    <>By {sub_product.designer.designer_name}, From {sub_product.supplier.supplier_name}</>
                                ) : sub_product.supplier?.supplier_name ? (
                                    <>From {sub_product.supplier.supplier_name}</>
                                ) : sub_product.designer?.designer_name ? (
                                    <>By {sub_product.designer.designer_name}</>
                                ) : null}
                            </span>
                        </div>

                        {/* Unavailability notice */}
                        <div className="bg-gray-5 border border-gray-10 rounded p-4 mt-4">
                            <p className="text-sm text-dark m-0">
                                {sub_product.not_available_message || 'This product is no longer available. Please see the similar products below or contact us for help with your selection.'}
                            </p>
                        </div>
                    </div>
                </div>

            </div>

            {/* Similar products */}
            {similarProducts.length > 0 && (
                <div className="mt-12">
                    <SimilarProductsCarousel products={similarProducts} />
                </div>
            )}

            {/* Recently viewed */}
            <RecentlyViewedProducts currentProductId={sub_product.sub_product_id} />

            {/* Reviews - historical reviews are still valuable */}
            <div id="reviews" className="mt-12">
                <Reviews
                    sub_product_id={sub_product.sub_product_id}
                    sub_product_name={decodeEntities(sub_product.sub_product_name)}
                    sub_product_thumb={sub_product.product_photo}
                    review_stats={sub_product.review_stats}
                    initialReviews={sub_product.reviews || []}
                />
            </div>
        </div>
    )
}

export default ProductContainerUnavailable
